/**
 * 入力を自動保存する画面の共通の仕組み（#177。#176 B3 で見積書に入れた flush と「送っている最中」の印を、どの画面でも使える形にした）。
 *   ① 送るのは1本ずつ：送っている最中（inflight）は重ねて送らない。送ったあとに中身が変わっていれば続けて送る。
 *      断られた（400 など）ときは、送ったあとに打った分があるときだけ送る（同じ中身で同じ断りをくり返さない）。つながらない・混み合い（502〜504）のときは 5 秒後にもう一度
 *   ② ready()：発行・PDF・送信・印刷の前に呼ぶ。書きかけを送り終えるのを待ち、保存できていなければ理由（文）を返す＝画面はその動きに進まず、
 *      saveGateText（contract）で理由を出す
 *   ③ 離れるとき（useLeaveGuard に登録）：送れていない直しは「続ける／破棄」。送る前・送っている最中は送り終えるのを待ってから移る（送れなければ確かめる）。
 *      画面の中のリンク・「‹ 戻る」・ブラウザの戻る／進む（⌘[・iPhone のスワイプ）・ページを閉じる（keepalive で送れない直しが残るとき）
 * 中身は画面が持つ（ref から読む）。この仕組みは「いつ・何本送るか」と「送れたか」だけを持つ。
 * 画面は 変えたら mark()（打つ欄は mark()、打ち終わりがはっきりしている操作は mark(true)）、保存の様子は state、離れるときの窓は dialog を描く
 */
import { SAVE_GATE_MSG } from "@genbaryoku/contract";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, messageOf } from "../api.js";
import { useLeaveGuard, type LeaveWatch } from "../components/BackButton.jsx";
import { productHeaders } from "./product.js";

export type SaveState = { kind: "idle" } | { kind: "saving" } | { kind: "saved"; at: string } | { kind: "error"; msg: string; retrying: boolean };
/** 断られたときの扱い（onError が返す）。msg＝上の帯に出す文（省けば API の文）、then＝stop（もう送らない。blocked で止める画面）／retry（5 秒後にもう一度） */
export type SaveErrorPlan = { msg?: string; then?: "stop" | "retry" };

const hhmm = () => new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

export type SaveQueue = {
  state: SaveState;
  /** 上の帯の様子を画面が直接変える（サーバーの答えで描き直したとき「保存しました」など） */
  setState: (s: SaveState | ((x: SaveState) => SaveState)) => void;
  /** 変えたら必ず呼ぶ。打つ欄は wait（0.5 秒）待ち、打ち続けても maxWait（1.5 秒）に1回は送る。immediate ならすぐ送る */
  mark: (immediate?: boolean) => void;
  /** いま送る（送っている最中なら何もしない＝終わってから続けて送る） */
  flush: () => Promise<void>;
  /** 書きかけを送り終えるまで待つ（送っている最中ならその終わりも）。保存できたら true */
  settle: () => Promise<boolean>;
  /** 発行・PDF・送信・印刷の前に。保存できていれば null、できていなければ理由の文 */
  ready: () => Promise<string | null>;
  /** 同じ「送っている最中」の印を立てて fn を送る（並べ方の保存など、ほかの保存と重ねない。終わったら待たせていた保存を送る） */
  exclusive: <T>(fn: () => Promise<T>) => Promise<T>;
  /** 書きかけを捨てる（下書きを削除したとき・読み直したとき） */
  discard: () => void;
  isDirty: () => boolean;
  isInflight: () => boolean;
  /** 送れていない直しがある（断られた・つながらない）。離れるときに確かめる */
  unsent: boolean;
  /** 離れるときの確かめの窓（画面が描く） */
  dialog: ReactNode;
};

export function useSaveQueue(opts: {
  /** いま画面にある中身を送る。投げたら「保存できませんでした」 */
  send: () => Promise<void>;
  /** 断られたときの扱い（省けば API の文。つながらないときは 5 秒後にもう一度） */
  onError?: (err: unknown) => SaveErrorPlan | void;
  /** 送らないあいだ（ほかで変更があって読み直しを待つ など） */
  blocked?: () => boolean;
  /** ページを閉じるときに keepalive で送る中身（大きすぎるときは null）。無い画面は、閉じると消える直しがあればブラウザの確かめ */
  keepalive?: () => { url: string; method: "PUT" | "PATCH"; body: string } | null;
  /** 打つ欄の待ち（既定 500ms）・打ち続けたときの上限（既定 1500ms。Infinity なら打ち終わるまで待つ） */
  wait?: number;
  maxWait?: number;
}): SaveQueue {
  const o = useRef(opts);
  o.current = opts;
  const dirty = useRef(false);
  const inflight = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const firstAt = useRef(0);
  /** 変えた回数（送ったあとに変わったかを見る） */
  const marks = useRef(0);
  const [state, setStateRaw] = useState<SaveState>({ kind: "idle" });
  const stateRef = useRef(state);
  const setState = useRef((s: SaveState | ((x: SaveState) => SaveState)) => {
    const next = typeof s === "function" ? s(stateRef.current) : s;
    stateRef.current = next;
    setStateRaw(next);
  }).current;

  const flushRef = useRef<() => Promise<void>>(async () => {});
  const flush = async (): Promise<void> => {
    window.clearTimeout(timer.current);
    if (!dirty.current || inflight.current || o.current.blocked?.()) return;
    dirty.current = false;
    inflight.current = true;
    const sentAt = marks.current;
    setState({ kind: "saving" });
    let failed = false;
    try {
      await o.current.send();
      // 送っている間にまた変えていたら、まだ「保存しました」と言わない（#93。続けて送る。finally）
      setState(dirty.current ? { kind: "saving" } : { kind: "saved", at: hhmm() });
    } catch (err) {
      dirty.current = true;
      failed = true;
      const plan = o.current.onError?.(err) ?? {};
      // つながらない（0）と、混み合い・配備の入れ替えの一瞬（502・503・504。性能の改善 F：API は混み合いを 503 DB_BUSY で返す）は、
      // 中身が悪いのではないので 5 秒後に同じ中身で送り直す
      const offline = err instanceof ApiError && (err.status === 0 || err.status === 502 || err.status === 503 || err.status === 504);
      const retry = plan.then === "retry" || (plan.then !== "stop" && offline);
      setState({ kind: "error", msg: plan.msg ?? messageOf(err), retrying: retry });
      if (retry) timer.current = window.setTimeout(() => void flushRef.current(), 5000); // つながらないときだけ、5秒後にもう一度
    } finally {
      inflight.current = false;
      // 送っている間にまた打っていたら、続けて送る。断られた（400 など）ときも、送ったあとに打った分があれば送る（#176 B3）。
      // 送った中身のまま（打ち直していない）なら、同じ断りをくり返さないよう送らない＝画面に残して、上の帯で知らせる（離れるときは確かめる）
      const changedSince = marks.current !== sentAt;
      if (dirty.current && !o.current.blocked?.() && (!failed || changedSince)) timer.current = window.setTimeout(() => void flushRef.current(), 500);
    }
  };
  flushRef.current = flush;

  const mark = useRef((immediate = false) => {
    const now = Date.now();
    if (!dirty.current) firstAt.current = now;
    dirty.current = true;
    marks.current++;
    // まだ送っていない変更がある間は、前の保存の「保存しました」を残さない（#93：それを見て開き直すと、最後の変更が消えていた）。
    // ただし止めているあいだ（blocked＝ほかで変更があって読み直しを待つ など）は送らないので、上の帯の誤り（「ほかで変更があります」）を
    // 「保存しています…」に戻さない（#179：戻すと、送っていないのに「保存しています…」のまま止まって見えた。e2e estimate-pc-compact ⑫ の揺れ）
    setState((x) => (x.kind === "saving" || (x.kind === "error" && o.current.blocked?.()) ? x : { kind: "saving" }));
    window.clearTimeout(timer.current);
    const wait = immediate || now - firstAt.current > (o.current.maxWait ?? 1500) ? 0 : (o.current.wait ?? 500);
    timer.current = window.setTimeout(() => void flushRef.current(), wait);
  }).current;

  const settle = useRef(async (): Promise<boolean> => {
    await sleep(0); // 行を閉じた結果（空の行を落とすなど）が画面の中身に入ってから
    for (let k = 0; k < 200; k++) {
      if (!dirty.current && !inflight.current) return true;
      if (inflight.current) await sleep(50);
      else {
        await flushRef.current();
        if (dirty.current && !inflight.current) return false; // 断られた・止めている
      }
    }
    return false;
  }).current;

  const ready = useRef(async (): Promise<string | null> => {
    if (await settle()) return null;
    const s = stateRef.current;
    return s.kind === "error" ? s.msg : SAVE_GATE_MSG.notYet;
  }).current;

  const exclusive = useRef(async <T,>(fn: () => Promise<T>): Promise<T> => {
    while (inflight.current) await sleep(50);
    inflight.current = true;
    try {
      return await fn();
    } finally {
      inflight.current = false;
      if (dirty.current && !o.current.blocked?.()) {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => void flushRef.current(), 0); // 待たせていた保存を送る
      }
    }
  }).current;

  const discard = useRef(() => {
    dirty.current = false;
  }).current;

  // 画面を離れるときに書きかけを送る
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      if (dirty.current) void flushRef.current();
    },
    [],
  );
  /**
   * ページを閉じる・開き直す・ほかのサイトへ移るとき（アンマウントが走らない）に、まだ送っていない変更を keepalive で送る（#93）。
   * keepalive はページが消えても届く送り方（本文は 64KB まで。大きすぎるときは画面が null を返し、今までどおり 0.5 秒の自動保存に頼る）。
   * 送っている最中（inflight）は送らない：古い保存があとから届いて新しいものを上書きしないように
   */
  useEffect(() => {
    const onHide = () => {
      const ka = o.current.keepalive;
      if (!ka || !dirty.current || inflight.current) return;
      window.clearTimeout(timer.current);
      const r = ka();
      if (!r) return;
      dirty.current = false;
      try {
        void fetch(r.url, { method: r.method, credentials: "include", keepalive: true, headers: { ...productHeaders(), "content-type": "application/json" }, body: r.body }).catch(() => undefined);
      } catch {
        dirty.current = true;
      }
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  const unsent = state.kind === "error";
  const watch: LeaveWatch = {
    unsent,
    pending: () => dirty.current || inflight.current,
    // 閉じると消える：keepalive で送れない（送っている最中のあとの直し・keepalive の無い画面）
    atRisk: () => dirty.current && (inflight.current || !o.current.keepalive),
    settle,
  };
  const guard = useLeaveGuard(watch);

  return {
    state,
    setState,
    mark,
    flush: () => flushRef.current(),
    settle,
    ready,
    exclusive,
    discard,
    isDirty: () => dirty.current,
    isInflight: () => inflight.current,
    unsent,
    dialog: guard.dialog,
  };
}
